import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import InlineSVG from "react-inlinesvg";
import { FUNCTION_ICONS, FUNCTION_NAMES, LEGACY_STYLE_INPUT_LIMIT } from "..";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../../AppEventPublisher.js";
import { useToast } from "../../ToastWrapper";
import CopyableTextarea from "../CopyableTextarea";
import StyleMatcher from "../StyleMatcher";
import TiptapField from "../../TiptapField";
import FeaturedStylesSelector from "./featuredStyles";
import { MovedStylePointerContainer, ReadOnlyStyleEditorBody, SelectedStyleLabel, StyleChooserContainer } from "./styles";
import { AnchorIds } from "../../StickyTour/TourTypes";

function SelectedStyleTitle({ name, source, onOpenStyleMenu }) {
  if (!name) return null;

  return (
    <SelectedStyleLabel
      role="button"
      tabIndex={0}
      data-testid="story-bible-selected-style"
      aria-label={`Open styles menu, ${name}`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenStyleMenu?.();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onOpenStyleMenu?.();
      }}
    >
      <span
        className="selected-style-name"
        data-testid="story-bible-selected-style-name"
      >
        {name}
      </span>
      {source === "sudowrite" && (
        <span
          className="selected-style-tag"
          data-testid="story-bible-selected-style-tag"
        >
          Sudowrite
        </span>
      )}
    </SelectedStyleLabel>
  );
}

function StyleHeader({ collapsed = false, onToggleCollapse, headingId, selectedStyleName, selectedStyleSource, onOpenStyleMenu }) {
  return (
    <header>
      {onToggleCollapse && (
        <div
          className="chevron-wrapper cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            onToggleCollapse();
          }}
        >
          <InlineSVG
            src="/assets/chevron-down.svg"
            className={classNames("collapse-chevron", { "rotated-chevron": collapsed })}
            aria-label={`${collapsed ? "Expand" : "Collapse"} Style`}
          />
        </div>
      )}
      <span
        id={headingId}
        className={onToggleCollapse ? "cursor-pointer" : ""}
        style={{ display: "flex", alignItems: "center", userSelect: onToggleCollapse ? "none" : undefined }}
        onClick={
          onToggleCollapse
            ? (e) => {
                e.preventDefault();
                onToggleCollapse();
              }
            : undefined
        }
      >
        <InlineSVG
          aria-hidden="true"
          src={FUNCTION_ICONS[FUNCTION_NAMES.STYLE]}
        ></InlineSVG>
        <span className="style-heading-text">Style</span>
        <SelectedStyleTitle
          name={selectedStyleName}
          source={selectedStyleSource}
          onOpenStyleMenu={onOpenStyleMenu}
        />
      </span>
    </header>
  );
}

const StyleChooser = ({ onFeaturedStylesClick, onMatchStyleClick, onCustomStyleClick, collapsed = false, onToggleCollapse, headingId, selectedStyleName, selectedStyleSource, onOpenStyleMenu }) => {
  const [showHoverIcons, setShowHoverIcons] = useState(false);

  return (
    <StyleChooserContainer
      id="style-chooser-container"
      $collapsed={collapsed}
      onMouseEnter={() => setShowHoverIcons(true)}
      onMouseLeave={() => setShowHoverIcons(false)}
      onFocus={() => setShowHoverIcons(true)}
      onBlur={() => setShowHoverIcons(false)}
      tabIndex={0}
    >
      <StyleHeader
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        headingId={headingId}
        selectedStyleName={selectedStyleName}
        selectedStyleSource={selectedStyleSource}
        onOpenStyleMenu={onOpenStyleMenu}
      />
      {!collapsed && (
        <section className="content">
          <div className="content-wrapper">
            <div className="content">
              <div
                className="action-btn"
                onClick={onFeaturedStylesClick}
              >
                <div className="title">
                  <InlineSVG
                    src="/assets/star.svg"
                    height={24}
                    width={24}
                  />
                  <span>Featured Styles</span>
                </div>
                <span>Tried and true</span>
              </div>
              <div
                className="action-btn"
                onClick={onMatchStyleClick}
              >
                <div className="title">
                  <InlineSVG
                    src="/assets/AI-sparkle.svg"
                    height={24}
                    width={24}
                  />
                  <span>Match My Style</span>
                </div>
                <span>Sounds like you</span>
              </div>
              <div
                className="action-btn"
                onClick={onCustomStyleClick}
              >
                <div className="title">
                  <InlineSVG
                    src="/assets/pencil.svg"
                    height={24}
                    width={24}
                  />
                  <span>Custom</span>
                </div>
                <span>For the most control</span>
              </div>
            </div>
          </div>
        </section>
      )}
    </StyleChooserContainer>
  );
};

const StyleComponentModes = {
  INITIAL: "INITIAL",
  DEFAULT: "DEFAULT",
};

function ReadOnlyStyleEditor({ value, collapsed = false, onToggleCollapse, headingId, selectedStyleName, selectedStyleSource, onOpenStyleMenu }) {
  return (
    <StyleChooserContainer
      data-testid="story-bible-readonly-style-editor"
      $collapsed={collapsed}
    >
      <StyleHeader
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        headingId={headingId}
        selectedStyleName={selectedStyleName}
        selectedStyleSource={selectedStyleSource}
        onOpenStyleMenu={onOpenStyleMenu}
      />
      {!collapsed && (
        <ReadOnlyStyleEditorBody>
          <TiptapField
            value={value}
            onChange={() => {}}
            readOnly
            ariaLabel="Style"
            searchableId="storybible-style"
            searchableName="Style"
          />
        </ReadOnlyStyleEditorBody>
      )}
    </StyleChooserContainer>
  );
}

function MovedStylePointer({ onTryStyles }) {
  const handleClick = () => {
    onTryStyles?.();
  };

  return (
    <MovedStylePointerContainer
      data-testid="story-bible-style-moved-pointer"
      aria-label="Style moved to top Styles control"
    >
      <span className="moved-style-pill">NEW</span>
      <InlineSVG
        className="moved-style-icon"
        src="/assets/style-guide/story-bible-style-icon.svg"
        aria-hidden="true"
      />
      <div className="moved-style-copy">
        <h3>Use styles for multiple projects</h3>
        <p>Use the Style menu at the top of the page to change your style as you write.</p>
      </div>
      <button
        type="button"
        className="moved-style-cta"
        onClick={handleClick}
      >
        <InlineSVG
          className="moved-style-cta-icon"
          src="/assets/style-guide/arrow-up.svg"
          aria-hidden="true"
        />
        Try it
      </button>
    </MovedStylePointerContainer>
  );
}

const Style = ({ innerRef, help, onOpenHistory, metadata, setStyle, onInput, onGenerate, project, featuredStyles, syncing = false, onBlur, collapsed = false, onToggleCollapse, onFocusCallback, headingId, showStyleGuidePointer = false, onStyleGuidePointerClick, selectedStyleName, selectedStyleSource, styleInputLimit = LEGACY_STYLE_INPUT_LIMIT }) => {
  const [styleMatcherOpen, setStyleMatcherOpen] = useState(false);
  const [featuredStylesOpen, setFeaturedStylesOpen] = useState(false);
  const [mode, setMode] = useState(StyleComponentModes.INITIAL);
  const modeChanged = useRef(false);
  const { setToast } = useToast();

  const handleMatchStyleClick = () => {
    setStyleMatcherOpen(true);
    AppEventPublisher.storyBible.action(StoryBibleType.style, StoryBibleAction.OpenMatchMyStyle);
    onGenerate?.();
  };

  const handleFeaturedStylesClick = () => {
    setFeaturedStylesOpen(true);
  };

  const handleFeaturedStyleSelect = (featuredStyle) => {
    setStyle(featuredStyle.style);
    setMode(StyleComponentModes.DEFAULT);
    AppEventPublisher.storyBible.style.selectedFeaturedStyle(featuredStyle.value);
    setFeaturedStylesOpen(false);
  };

  const handleCustomStyleClick = () => {
    setMode(StyleComponentModes.DEFAULT);
    AppEventPublisher.storyBible.action(StoryBibleType.style, StoryBibleAction.SelectedCustomStyle);
  };


  const stylePlaceholder = `Describe the style of prose that you want to influence Scenes and Draft.
Example: Limited POV. No clichés. Instead, fast-paced, with varied sentence lengths (some very short). Sharp dialogue, intense tone, fast pacing, and cinematic feeling. Focus on action`;

  const isReadOnlySudowriteStyle = showStyleGuidePointer && selectedStyleSource === "sudowrite";
  const headerStyleName = showStyleGuidePointer ? selectedStyleName : undefined;
  const selectedStyleTitle = (
    <SelectedStyleTitle
      name={headerStyleName}
      source={selectedStyleSource}
      onOpenStyleMenu={onStyleGuidePointerClick}
    />
  );

  useEffect(() => {
    if (metadata?.style && !modeChanged.current) {
      setMode(StyleComponentModes.DEFAULT);
      modeChanged.current = true;
    }
  }, [metadata?.style]);

  return (
    <>
      {showStyleGuidePointer && !collapsed && (
        <MovedStylePointer onTryStyles={onStyleGuidePointerClick} />
      )}
      {isReadOnlySudowriteStyle ? (
        <ReadOnlyStyleEditor
          value={metadata?.style || ""}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          headingId={headingId}
          selectedStyleName={headerStyleName}
          selectedStyleSource={selectedStyleSource}
          onOpenStyleMenu={onStyleGuidePointerClick}
        />
      ) : mode === StyleComponentModes.INITIAL ? (
        <StyleChooser
          onCustomStyleClick={handleCustomStyleClick}
          onFeaturedStylesClick={handleFeaturedStylesClick}
          onMatchStyleClick={handleMatchStyleClick}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          headingId={headingId}
          selectedStyleName={headerStyleName}
          selectedStyleSource={selectedStyleSource}
          onOpenStyleMenu={onStyleGuidePointerClick}
        />
      ) : null}
      {!isReadOnlySudowriteStyle && mode === StyleComponentModes.DEFAULT && (
        <CopyableTextarea
          innerRef={innerRef}
          title="Style"
          titleAccessory={selectedStyleTitle}
          fieldKey="style"
          type={FUNCTION_NAMES.STYLE}
          help={help}
          history={onOpenHistory}
          placeholder={stylePlaceholder}
          value={metadata?.style || ""}
          setValue={setStyle}
          onInput={onInput}
          onBlur={onBlur}
          maxLength={styleInputLimit === null ? undefined : styleInputLimit}
          iconPath={FUNCTION_ICONS[FUNCTION_NAMES.STYLE]}
          height="short"
          ariaLabel="Style"
          containerClassName={"story-bible"}
          showDotsMenu={true}
          syncing={syncing}
          onFocusCallback={onFocusCallback}
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          headingId={headingId}
          dotsMenuAnchorId={AnchorIds.STORY_BIBLE_STYLE_MENU}
          actions={[
            {
              label: "Start Over",
              icon: (
                <InlineSVG
                  src="/assets/restart.svg"
                  aria-hidden="true"
                />
              ),
              onClick: () => setMode(StyleComponentModes.INITIAL),
              danger: true,
            },
          ]}
        />
      )}
      {styleMatcherOpen && (
        <StyleMatcher
          close={() => setStyleMatcherOpen(false)}
          setError={setToast}
          setStyle={async (value, options) => {
            await setStyle(value, options);
            // Flip to the editor explicitly, like Featured/Custom do. The
            // metadata effect below is one-shot per mount (modeChanged), so it
            // won't fire again after Start Over or when a style existed at mount.
            setMode(StyleComponentModes.DEFAULT);
            modeChanged.current = true;
          }}
          project={project}
          variant={showStyleGuidePointer ? "styleGuide" : "default"}
        />
      )}
      {featuredStylesOpen && (
        <FeaturedStylesSelector
          isOpen={featuredStylesOpen}
          setIsOpen={setFeaturedStylesOpen}
          onStyleSelect={handleFeaturedStyleSelect}
          writingStyles={featuredStyles}
        />
      )}
    </>
  );
};

export default Style;
